import React, { useContext, useEffect, useState } from 'react'
import { FormControl, TextField, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MoreHorizOutlined } from '@mui/icons-material';
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import { queueTypeOptions } from '../../../constants/constantList';
import { debounce, set } from 'lodash';
import { decryptString, encryptString, stringifyJSONAttributes, stringToBooleanConversion } from '../../../utils/utilFunctions';
import SmartPasswordField from '../../../commonComponents/SmartPasswordField';
import MsmqHeaderFields from './MsmqHeaderFields';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';
import _ from 'lodash';
import { ParentScreenCtx } from '../../../pages/workbench_build/WorkbenchBuild';
import { useSelector } from 'react-redux';

const Msmq = ({ connectionDetails, handleBodyContent, direction, screen, disableControl, useConnection, repoPDC, source = "" }) => {
  const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
    const { t } = useTranslation();
    const [queueType, setQueueType] = useState('');
    const [queueName, setQueueName] = useState('');
    const [url, setUrl] = useState('');
    const [domain, setDomain] = useState('');
    const [username, setUsername] = useState(null);
    const [password, setPassword] = useState(null);
    const [windowAuth, setWindowAuth] = useState(false);
    const [ntlm, setNtlm] = useState(false);
    const [deadLetter, setDeadLetter] = useState(false);
    const [transactional, setTransactional] = useState(false);
    const [commit, setCommit] = useState('AUTO_COMMIT');
    const [content, setContent] = useState(null);
    const [openHeaderModal, setOpenHeaderModal] = useState(false);
    const [workflowSessionModal, setWorkflowSessionModal] = useState({ open: false, isFirstTime: false });
    const [valueLabel, setValueLabel] = useState("");
    const parentScreen = useContext(ParentScreenCtx);
    const selectedNode = useSelector(state => state?.treeData[parentScreen]?.rightTree?.tree?.body?.selectedNode || {});

    const removeUndefinedAndNullKeys = (obj = {}) => {
        const tempObj = {...obj}
        Object.keys(tempObj || {}).forEach(e => {
            if(tempObj[e] === undefined || tempObj[e] === null) delete tempObj[e];
        })
        return tempObj
    }

    const debouncedContentUpdate = debounce(handleBodyContent, 200);

    // getFontWeights function following the pattern of other adapter types
    const getFontWeight = (control, currentValue, useConnection) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue &&
          useConnection === "preDefined"
          ? "bold"
          : "normal";
    };

    const fontWeights = {
        queueType: getFontWeight("PRIVATE_PUBLIC", queueType, useConnection),
        queueName: getFontWeight("QUEUE_NAME", queueName, useConnection),
        url: getFontWeight("URL", url, useConnection),
        domain: getFontWeight("DOMAIN", domain, useConnection),
        username: getFontWeight("USERNAME", username, useConnection),
        password: getFontWeight("PASSWORD", password, useConnection),
        commit: getFontWeight("MSMQ_AUTOCOMMIT", commit, useConnection)
    };

    const onInputChange = (name, value, bulk) => {
        if (screen === 'GET_DATA' || screen === 'SEND_DATA') {
            if(bulk){
                handleBodyContent({ add: bulk });
                return;
            }
            setContent((prev) => ({[name]: value}));
        } else {
            setContent((prev) => ({ ...prev, [name]: value }));
        }
    }
    const handleUseWindowsAuthChange = (checked) => {
        setWindowAuth(checked);
        setDomain("");
        setUsername("");
        setPassword("");
        if(screen === "GET_DATA" || screen === "SEND_DATA") {
            onInputChange(null,null, {
                useNTLMv1: stringifyJSONAttributes(checked),
                DOMAIN: null,
                USERNAME: null,
                PASSWORD: null
            })
        } else {
            onInputChange("useNTLMv1", stringifyJSONAttributes(checked));
            onInputChange("DOMAIN", "");
            onInputChange("USERNAME", "");
            onInputChange("PASSWORD", "");
        }
    }
    useEffect(() => {
        if(!_.isEqual(removeUndefinedAndNullKeys(content), removeUndefinedAndNullKeys(connectionDetails))) {
            debouncedContentUpdate(content);
        }
    }, [content]);

    useEffect(() => {
        setQueueType(connectionDetails?.PRIVATE_PUBLIC || 'PRIVATE');
        setQueueName(connectionDetails?.QUEUE_NAME || '');
        setUrl(connectionDetails?.URL || '');
        setDomain(connectionDetails?.DOMAIN || '');
        setUsername(connectionDetails?.USERNAME || '');
        if(screen === "GET_DATA" || screen === "SEND_DATA") {
			setPassword(
        shouldEncryptDecryptPassword
          ? (decryptString((connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || ""),process.env.REACT_APP_SECRET_KEY2)) || ""
          : (connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || "")
      );
		}else {
			setPassword(connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || '');
		}
        setWindowAuth(stringToBooleanConversion(connectionDetails?.useNTLMv1));
        setNtlm(stringToBooleanConversion(connectionDetails?.NTLMv2));
        setDeadLetter(stringToBooleanConversion(connectionDetails?.DEADLETTER));
        setTransactional(stringToBooleanConversion(connectionDetails?.TRANSACTIONAL));
        if (stringToBooleanConversion(connectionDetails?.MSMQ_AUTOCOMMIT)) setCommit('AUTO_COMMIT');
        else setCommit('TRANSACTIONAL_COMMIT');
        setContent({ ...connectionDetails })
    }, [connectionDetails]);

    const queueTypeDropDownItems = queueTypeOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
            {t(obj.text)}
        </MenuItem>
    ));

    const setMsmqHeaders = (headers) => {
        const transformedHeaders = Object?.keys(headers)?.reduce((acc, curr) => {
            if(headers[curr] === '') {
                acc[curr] = null;
            } else {
                acc[curr] = headers[curr];
            }
            return acc;
        }, {});
        onInputChange(null, null, transformedHeaders);
        setOpenHeaderModal(false);
    }

    return (
      <>
        <MsmqHeaderFields
          open={openHeaderModal}
          handleClose={() => setOpenHeaderModal(false)}
          disableControl={disableControl}
          connectionDetails={selectedNode?.OUTBOUND || {}}
          valuesHandler={setMsmqHeaders}
        />
        <FormControl variant="outlined" className="width-100" size="small">
          <InputLabel id="queueTypeDropdownLabel">
            {t("LBL_QUEUE_TYPE")}
          </InputLabel>
          <Select
            className={"width-100"}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
            name={"PRIVATE_PUBLIC"}
            labelId="queueTypeDropdownLabel"
            label={t("LBL_QUEUE_TYPE")}
            id="queueTypeDropdown"
            value={queueType || ""}
            disabled={disableControl}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setQueueType(e.target.value);
            }}
            sx={{ "& .MuiSelect-select": { fontWeight: fontWeights.queueType } }}>
            {queueTypeDropDownItems}
          </Select>
        </FormControl>
        <div style={{ display: "flex", gap: "4px" }}>
          <FormControl className="width-100" size="small">
            <TextField
              variant="outlined"
              size="small"
              label={t("LBL_QUEUE_NAME")}
              type="text"
              value={queueName}
              disabled={disableControl}
              onChange={(e) => {
                onInputChange(e.target.name, e.target.value);
                setQueueName(e.target.value);
              }}
              className={"width-100"}
              name={"QUEUE_NAME"}
              autoComplete="off"
              inputProps={{
                style: { fontWeight: fontWeights.queueName },
              }}
            />
          </FormControl>
          {(screen === "GET_DATA" || screen === "SEND_DATA") && (
            <Button
              size="small"
              disabled={disableControl || false}
              variant="outlined"
              onClick={() => {
                setValueLabel(t("LBL_QUEUE_NAME"));
                setWorkflowSessionModal({ open: true, isFirstTime: true });
              }}
              sx={{ width: "40px", minWidth: "40px" }}>
              <MoreHorizOutlined fontSize="small" />
            </Button>
          )}
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          <FormControl className="width-100" size="small">
            <TextField
              variant="outlined"
              size="small"
              label={t("LBL_URL")}
              type="text"
              value={url}
              disabled={disableControl}
              onChange={(e) => {
                onInputChange(e.target.name, e.target.value);
                setUrl(e.target.value);
              }}
              className={"width-100"}
              name={"URL"}
              autoComplete="off"
              inputProps={{
                style: { fontWeight: fontWeights.url },
              }}
            />
          </FormControl>
          {((screen === "GET_DATA" || screen === "SEND_DATA") && (connectionDetails?.ADAPTER_TYPE === "MSMQ")) && (
            <Button
              size="small"
              disabled={disableControl || false}
              variant="outlined"
              onClick={() => {
                setValueLabel(t("LBL_URL"));
                setWorkflowSessionModal({ open: true, isFirstTime: true });
              }}
              sx={{ width: "40px", minWidth: "40px" }}>
              <MoreHorizOutlined fontSize="small" />
            </Button>
          )}
        </div>
        <div className="flex flex-wrap">
          <TPCheckbox
            label={t("USE_WINDOWS_AUTH")}
            checked={windowAuth}
            disabled={disableControl}
            handleCheck={(checked) => {
              handleUseWindowsAuthChange(checked);
            }}
          />
          <TPCheckbox
            label={t("SUPPORT_NTLMV2")}
            checked={ntlm}
            disabled={disableControl}
            handleCheck={(checked) => {
              onInputChange("NTLMv2", stringifyJSONAttributes(checked));
              setNtlm(checked);
            }}
          />
        </div>
        {!windowAuth && (
          <>
            <div style={{ display: "flex", gap: "4px" }}>
              <FormControl className="width-100" size="small">
                <TextField
                  variant="outlined"
                  size="small"
                  label={t("LBL_DOMAIN")}
                  type="text"
                  value={domain}
                  onChange={(e) => {
                    onInputChange(e.target.name, e.target.value);
                    setDomain(e.target.value);
                  }}
                  className={"width-100"}
                  name={"DOMAIN"}
                  disabled={windowAuth || disableControl}
                  autoComplete="off"
                  inputProps={{
                    style: { fontWeight: fontWeights.domain },
                  }}
                />
              </FormControl>
              {(screen === "GET_DATA" || screen === "SEND_DATA") && (
                <Button
                  size="small"
                  disabled={disableControl || false}
                  variant="outlined"
                  onClick={() => {
                    setValueLabel(t("LBL_DOMAIN"));
                    setWorkflowSessionModal({ open: true, isFirstTime: true });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <FormControl className="width-100" size="small">
                <TextField
                  variant="outlined"
                  size="small"
                  label={t("LBL_USERNAME")}
                  type="text"
                  value={username || ""}
                  onChange={(e) => {
                    onInputChange("USERNAME", e.target.value);
                    setUsername(e.target.value);
                  }}
                  className={"width-100"}
                  name={"USER_NAME"}
                  disabled={windowAuth || disableControl}
                  autoComplete="off"
                  inputProps={{
                    style: { fontWeight: fontWeights.username },
                  }}
                />
              </FormControl>
              {(screen === "GET_DATA" || screen === "SEND_DATA") && (
                <Button
                  size="small"
                  disabled={disableControl || false}
                  variant="outlined"
                  onClick={() => {
                    setValueLabel(t("LBL_USERNAME"));
                    setWorkflowSessionModal({ open: true, isFirstTime: true });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <SmartPasswordField
                variant="outlined"
                size="small"
                label={t("LBL_WS_PASSWORD")}
                value={password || ""}
                onChange={(e) => {
                  onInputChange(
                    "PASSWORD",
                    screen === "GET_DATA" || screen === "SEND_DATA"
                      ? (shouldEncryptDecryptPassword
                        ? encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2)
                        : e.target.value)
                      : e.target.value
                  );
                  setPassword(e.target.value);
                }}
                className={"width-100"}
                name={"PASS_WORD"}
                disabled={windowAuth || disableControl}
                autoComplete="off"
                inputProps={{
                  style: { fontWeight: fontWeights.password },
                }}
              />
              {(screen === "GET_DATA" || screen === "SEND_DATA") && (
                <Button
                  size="small"
                  disabled={disableControl || false}
                  variant="outlined"
                  onClick={() => {
                    setValueLabel(t("LBL_WS_PASSWORD"));
                    setWorkflowSessionModal({ open: true, isFirstTime: true });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              )}
            </div>
          </>
        )}
        {["SEND_DATA"].includes(screen) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOpenHeaderModal(true)}
            disabled={disableControl}>
            {t("LBL_MQ_HEADER_FIELDS")}
          </Button>
        )}
        <div className="flex flex-wrap">
          {direction !== "OUT" && (
            <TPCheckbox
              label={t("DEADLETTER")}
              checked={deadLetter}
              disabled={disableControl}
              handleCheck={(checked) => {
                onInputChange("DEADLETTER", stringifyJSONAttributes(checked));
                setDeadLetter(checked);
              }}
            />
          )}
          <TPCheckbox
            label={t("TRANSACTIONAL")}
            checked={transactional}
            disabled={disableControl}
            handleCheck={(checked) => {
              onInputChange("TRANSACTIONAL", stringifyJSONAttributes(checked));
              setTransactional(checked);
            }}
          />
        </div>
        <div className="flex">
          <FormControl disabled={!transactional || disableControl}>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="MSMQ_AUTOCOMMIT"
              className="flex justify-center"
              value={commit || ""}
              onChange={(e) => {
                if (e.target.value === "AUTO_COMMIT") {
                  onInputChange(e.target.name, "TRUE");
                } else onInputChange(e.target.name, "FALSE");
                setCommit(e.target.value);
              }}>
              <FormControlLabel
                value="AUTO_COMMIT"
                control={<Radio />}
                label={t("AUTO_COMMIT")}
                sx={{ "& .MuiFormControlLabel-label": { fontWeight: fontWeights.commit } }}
              />
              <FormControlLabel
                value="TRANSACTIONAL_COMMIT"
                control={<Radio />}
                label={t("LBL_TRANSACTIONAL_COMMIT")}
                sx={{ "& .MuiFormControlLabel-label": { fontWeight: fontWeights.commit } }}
              />
            </RadioGroup>
          </FormControl>
        </div>
        <WorkflowSessionModal
          valueLabel={valueLabel}
          showAccountData={true}
          showCallingWorkflow={true}
          showSystemData={true}
          showWorkflowSession={true}
          showDateAndTime={true}
          openModal={workflowSessionModal}
          setModalOpen={setWorkflowSessionModal}
          handleClose={() =>
            setWorkflowSessionModal({ open: false, isFirstTime: false })
          }
          initialValue={
            valueLabel === t("LBL_QUEUE_NAME")
              ? queueName
              : valueLabel === t("LBL_URL")
              ? url
              : valueLabel === t("LBL_DOMAIN")
              ? domain
              : valueLabel === t("LBL_USERNAME")
              ? username
              : valueLabel === t("LBL_WS_PASSWORD")
              ? password
              : ""
          }
          inputType={valueLabel === t("LBL_WS_PASSWORD") ? "password" : "text"}
          specialPasswordField={valueLabel === t("LBL_WS_PASSWORD")}
          valuesHandler={(value) => {
            if (valueLabel === t("LBL_QUEUE_NAME")) {
              onInputChange("QUEUE_NAME", value);
              setQueueName(value);
            } else if (valueLabel === t("LBL_URL")) {
              onInputChange("URL", value);
              setUrl(value);
            } else if (valueLabel === t("LBL_DOMAIN")) {
              onInputChange("DOMAIN", value);
              setDomain(value);
            } else if (valueLabel === t("LBL_USERNAME")) {
              onInputChange("USERNAME", value);
              setUsername(value);
            } else if (valueLabel === t("LBL_WS_PASSWORD")) {
              onInputChange(
                "PASSWORD",
                shouldEncryptDecryptPassword
                  ? encryptString(value, process.env.REACT_APP_SECRET_KEY2)
                  : value
              );
              setPassword(value);
            }
          }}
        />
      </>
    );
}

export default Msmq